export const dealValueSegmentLabels = ["Small", "Medium", "Large"] as const;

export const dealValueSegmentPresentationOrder = ["Large", "Medium", "Small"] as const;

export type DealValueSegment = typeof dealValueSegmentLabels[number];

const dealValueSegmentPresentationRank = new Map<string, number>(
  dealValueSegmentPresentationOrder.map((label, index) => [label, index]),
);

export function compareDealValueSegmentLabels(a: string, b: string) {
  const aRank = dealValueSegmentPresentationRank.get(a);
  const bRank = dealValueSegmentPresentationRank.get(b);
  if (aRank !== undefined || bRank !== undefined) {
    if (aRank === undefined) return 1;
    if (bRank === undefined) return -1;
    return aRank - bRank;
  }
  if (a === "Not specified" || b === "Not specified") {
    if (a === b) return 0;
    return a === "Not specified" ? 1 : -1;
  }
  return a.localeCompare(b);
}

export function orderDealValueSegmentRows<T extends { label: string }>(rows: readonly T[]) {
  return rows.toSorted((a, b) => compareDealValueSegmentLabels(a.label, b.label));
}

type DealValueSegmentInput = {
  id: number | string;
  value: number;
};

/**
 * Split positive-value Deals into three ranked, equal-sized cohorts.
 * Values must already be normalized to company currency by the caller.
 */
export function buildDealValueSegmentMap<TDeal extends DealValueSegmentInput>(
  deals: TDeal[],
) {
  const eligible = deals
    .filter(deal => Number.isFinite(deal.value) && deal.value > 0)
    .toSorted((a, b) => (
      a.value - b.value || String(a.id).localeCompare(String(b.id))
    ));
  const segments = new Map<string, DealValueSegment>();

  for (const [index, deal] of eligible.entries()) {
    const segment = dealValueSegmentLabels[
      Math.min(2, Math.floor(index * 3 / eligible.length))
    ];
    segments.set(String(deal.id), segment);
  }

  return segments;
}
